import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Utility function to merge Tailwind CSS classes with proper precedence
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Normalize a date value from an HTML date input for the backend Zod datetime() schema.
 * - Plain YYYY-MM-DD (from input[type=date]) → appends T00:00:00.000Z
 * - Already-ISO string (contains T) → returned unchanged
 * - Empty / falsy → returns undefined (Zod skips optional field)
 */
export function toISODatetime(val?: string): string | undefined {
  if (!val) return undefined;
  return val.includes('T') ? val : `${val}T00:00:00.000Z`;
}

/**
 * Returns today's date as YYYY-MM-DD in the user's local timezone.
 * Uses explicit date component extraction (not locale-dependent formatting).
 * Used for holdStartDate and any field that needs the user's "today".
 */
export function getLocalTodayYMD(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Serialises a hold expiration date value to a YYYY-MM-DD string for API submission.
 *
 * Handles two input formats produced by the DatePicker:
 *   - YYYY-MM-DD (e.g. "2026-05-25") — returned as-is after calendar validation.
 *   - M/d/yyyy   (e.g. "5/25/2026")  — parsed explicitly via regex; never new Date().
 *
 * Returns undefined if the input is empty, unparseable, not a real calendar date,
 * or matches the default expiration date (so the backend calculates the default).
 *
 * Does NOT use new Date(val) for non-ISO strings — browser Date parsing of M/d/yyyy
 * is implementation-dependent (notably inconsistent in Safari) and silently coerces
 * invalid dates like 2/31/2026 or produces NaN-NaN-NaN on Invalid Date.
 */
export function serializeHoldExpirationDate(
  holdExpirationDate: string,
  defaultHoldExpirationDate: string
): string | undefined {
  if (!holdExpirationDate) return undefined;

  /**
   * Parse a date string to YYYY-MM-DD without using new Date() for non-ISO inputs.
   * Supports YYYY-MM-DD and M/d/yyyy (or M/d/yy).
   * Returns null if the string is unparseable or not a real calendar date.
   */
  const toYMD = (val: string): string | null => {
    const trimmed = val.trim();
    if (!trimmed) return null;

    let year: number, month: number, day: number;

    // YYYY-MM-DD — extract components directly, no Date parsing needed
    const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) {
      year = Number(isoMatch[1]);
      month = Number(isoMatch[2]);
      day = Number(isoMatch[3]);
    } else {
      // M/d/yyyy or M/d/yy — explicit regex, never new Date()
      const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
      if (!slashMatch) return null;
      month = Number(slashMatch[1]);
      day = Number(slashMatch[2]);
      year = Number(slashMatch[3]);
      if (year < 100) year += 2000;
    }

    // Reject invalid calendar dates (e.g. Feb 31, month 13)
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    const check = new Date(year, month - 1, day);
    if (check.getFullYear() !== year || check.getMonth() !== month - 1 || check.getDate() !== day) {
      return null;
    }

    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const selectedYMD = toYMD(holdExpirationDate);
  if (!selectedYMD) return undefined; // unparseable or invalid — omit from payload

  const defaultYMD = toYMD(defaultHoldExpirationDate);

  // Omit when unchanged — let the backend calculate the default
  if (defaultYMD && selectedYMD === defaultYMD) return undefined;

  return selectedYMD;
}

/**
 * Parses a date string (M/d/yyyy or YYYY-MM-DD) to a YYYY-MM-DD string
 * using local date components — never new Date(string) for non-ISO inputs.
 * Returns null if the input is unparseable or not a real calendar date.
 * Used for timezone-safe comparisons (e.g. against maxHoldExpirationDate).
 */
export function parseDateToLocalYMD(val: string): string | null {
  const trimmed = val.trim();
  if (!trimmed) return null;

  let year: number, month: number, day: number;

  const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    year = Number(isoMatch[1]);
    month = Number(isoMatch[2]);
    day = Number(isoMatch[3]);
  } else {
    const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
    if (!slashMatch) return null;
    month = Number(slashMatch[1]);
    day = Number(slashMatch[2]);
    year = Number(slashMatch[3]);
    if (year < 100) year += 2000;
  }

  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const check = new Date(year, month - 1, day);
  if (check.getFullYear() !== year || check.getMonth() !== month - 1 || check.getDate() !== day) {
    return null;
  }
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * Format a calendar date (DATE columns) in UTC to avoid timezone shifts.
 * Use for hold dates, flight dates, expiration dates — any date-only field.
 */
export function formatDate(date: string | Date): string {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * Format a timestamp (TIMESTAMPTZ columns) in the user's local timezone.
 * Use for created_at, updated_at, assigned_at, audit timestamps — any
 * field that represents a point in time the user cares about locally.
 */
export function formatDateTime(date: string | Date): string {
  return new Date(date).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format a timestamp (TIMESTAMPTZ columns) as a date-only string in the user's local timezone.
 * Use for created_at, updated_at when you want "May 20, 2026" without time,
 * but need the date to reflect the user's local day (not UTC).
 */
export function formatLocalDate(date: string | Date | null | undefined): string {
  if (!date) return 'Unknown date';

  const parsed = new Date(date);
  if (isNaN(parsed.getTime())) return 'Invalid date';

  return parsed.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format user role for display
 */
export function formatRole(role: string): string {
  return role
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Format status for display
 * OPE-386: pass optional workflowStatus — when 'cancelled' it takes precedence over derivedStatus
 * so every render site displays "Cancelled" regardless of the frozen derivedStatus value.
 */
export function formatStatus(status: string, workflowStatus?: string): string {
  if (workflowStatus === 'cancelled') return 'Cancelled';
  return status
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Get status color class for Tailwind
 * OPE-386: pass optional workflowStatus — when 'cancelled' returns a consistent red token
 * so every render site agrees on the colour regardless of the frozen derivedStatus value.
 */
export function getStatusColor(status: string, workflowStatus?: string): string {
  if (workflowStatus === 'cancelled') return 'bg-[#FEE2E2] text-[#B91C1C]';
  const statusColors: Record<string, string> = {
    open: 'bg-[#EEE9F9] text-[#6B4FBB]',
    in_progress: 'bg-[#FEF3C7] text-[#92400E]',
    completed: 'bg-[#E8F5ED] text-[#1A7A3C]',
    approved: 'bg-[#E8F5ED] text-[#1A7A3C]',
    in_review: 'bg-[#EEE9F9] text-[#6B4FBB]',
    not_approved: 'bg-[#FEE2E2] text-[#B91C1C]',
    approval_required_by_exception: 'bg-[#EEE9F9] text-[#6B4FBB]',
    revision_required_by_sales: 'bg-[#FEE2E2] text-[#B91C1C]',
    partial_response_received: 'bg-[#FEF3C7] text-[#92400E]',
    request_completed_active: 'bg-[#E8F5ED] text-[#1A7A3C]',
    request_expired: 'bg-[#EFEFEF] text-[#555555]',
    needs_review: 'bg-[#FEF3C7] text-[#92400E]',
    cancelled: 'bg-[#FEE2E2] text-[#B91C1C]',
  };

  return statusColors[status] || 'bg-[#EFEFEF] text-[#555555]';
}

/**
 * Truncate text with ellipsis
 */
export function truncate(text: string, length: number): string {
  if (text.length <= length) return text;
  return text.slice(0, length) + '...';
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };

    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}
